import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2, ArrowRight, Printer } from "lucide-react";
import { imprimirRemitoDeposito } from "./RemitoDeposito";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-cyan-500/50";

/** Transferencia de mercadería entre depósitos de la misma empresa.
 *  props: token, logo, depositos[], onClose, onDone, inicial?{origen_id, producto_id, variante_id} */
export default function TransferenciaDepositoModal({ token, logo, depositos, onClose, onDone, inicial }) {
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [origen, setOrigen] = useState(inicial?.origen_id || depositos[0]?.id || "");
  const [destino, setDestino] = useState("");
  const [search, setSearch] = useState("");
  const [disponibles, setDisponibles] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [notas, setNotas] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    if (!origen) { setDisponibles([]); return; }
    const q = new URLSearchParams({ logo_tipo: logo || "" });
    if (search.trim()) q.set("search", search.trim());
    const r = await fetch(`${API}/admin/depositos/${origen}/productos?${q}`, { headers });
    setDisponibles(r.ok ? await r.json() : []);
  }, [origen, search, logo, token]); // eslint-disable-line

  useEffect(() => { const t = setTimeout(cargar, 250); return () => clearTimeout(t); }, [cargar]);
  useEffect(() => { setLineas([]); }, [origen]);

  // producto inicial (desde la ficha del producto)
  useEffect(() => {
    if (!inicial?.producto_id || !disponibles.length || lineas.length) return;
    const it = disponibles.find(d => d.producto_id === inicial.producto_id && (d.variante_id || null) === (inicial.variante_id || null));
    if (it) setLineas([{ ...it, cantidad: "" }]);
  }, [disponibles]); // eslint-disable-line

  const key = (x) => `${x.producto_id}|${x.variante_id || ""}`;
  const agregar = (it) => {
    if (lineas.some(l => key(l) === key(it))) return;
    setLineas(l => [...l, { ...it, cantidad: "" }]);
  };
  const setCant = (k, v) => setLineas(ls => ls.map(l => key(l) === k ? { ...l, cantidad: v } : l));

  const total = lineas.reduce((s, l) => s + (parseFloat(l.cantidad) || 0), 0);

  const transferir = async () => {
    if (!origen || !destino) { toast.error("Elegí el depósito de origen y de destino"); return; }
    if (origen === destino) { toast.error("Origen y destino deben ser distintos"); return; }
    if (!lineas.length) { toast.error("Agregá al menos un producto"); return; }
    for (const l of lineas) {
      const c = parseFloat(l.cantidad) || 0;
      if (c <= 0) { toast.error(`Indicá la cantidad de ${l.nombre}`); return; }
      if (c > l.disponible + 1e-9) { toast.error(`${l.nombre}: solo hay ${l.disponible} disponible en el origen`); return; }
    }
    setGuardando(true);
    try {
      const r = await fetch(`${API}/admin/depositos/transferencias`, {
        method: "POST", headers,
        body: JSON.stringify({
          logo_tipo: logo, origen_id: origen, destino_id: destino, notas: notas || null,
          items: lineas.map(l => ({ producto_id: l.producto_id, variante_id: l.variante_id || null, cantidad: parseFloat(l.cantidad) })),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "No se pudo transferir");
      toast.success(`Transferencia ${d.numero} realizada`);
      const full = await fetch(`${API}/admin/depositos/transferencias/${d.id}`, { headers });
      if (full.ok) imprimirRemitoDeposito(await full.json());
      onDone && onDone(d);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const destinos = depositos.filter(d => d.id !== origen);

  return (
    <div className="fixed inset-0 z-[250] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-heading text-white text-base">Transferir mercadería entre depósitos</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
            <div>
              <label className="block text-slate-400 text-xs mb-1">Depósito de origen</label>
              <select value={origen} onChange={e => { setOrigen(e.target.value); if (e.target.value === destino) setDestino(""); }} className={inp}>
                {depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
              </select>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 mb-2 hidden md:block" />
            <div>
              <label className="block text-slate-400 text-xs mb-1">Depósito de destino</label>
              <select value={destino} onChange={e => setDestino(e.target.value)} className={inp}>
                <option value="">Seleccionar...</option>
                {destinos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-white/10 rounded-xl p-3">
              <p className="text-slate-300 text-xs font-medium mb-2">Productos con stock en el origen</p>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, SKU o código de barras..." className={inp} />
              <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-white/5">
                {disponibles.length === 0 && <p className="text-slate-500 text-xs py-4 text-center">Sin productos con stock en este depósito.</p>}
                {disponibles.map(it => (
                  <button key={key(it)} onClick={() => agregar(it)} className="w-full text-left flex items-center justify-between gap-2 py-2 px-1 hover:bg-white/5 rounded">
                    <span className="text-xs text-white">{it.nombre}{it.variante_texto ? ` · ${it.variante_texto}` : ""}
                      <span className="text-slate-500"> {it.sku}{it.ubicacion ? ` · ${it.ubicacion}` : ""}</span></span>
                    <span className="flex items-center gap-2 text-xs text-cyan-300 whitespace-nowrap">{it.disponible} {it.unidad}<Plus className="w-3.5 h-3.5" /></span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-white/10 rounded-xl p-3">
              <p className="text-slate-300 text-xs font-medium mb-2">A transferir</p>
              {lineas.length === 0 ? <p className="text-slate-500 text-xs py-4 text-center">Elegí productos de la lista.</p> : (
                <table className="w-full text-xs">
                  <thead className="text-slate-500"><tr><th className="text-left py-1">Producto</th><th className="text-right w-24">Cantidad</th><th className="w-6"></th></tr></thead>
                  <tbody>
                    {lineas.map(l => (
                      <tr key={key(l)} className="border-t border-white/5">
                        <td className="py-1.5 text-white">{l.nombre}{l.variante_texto ? ` · ${l.variante_texto}` : ""}<div className="text-slate-500">disp. {l.disponible} {l.unidad}</div></td>
                        <td className="text-right"><input type="number" min="0" step="any" max={l.disponible} value={l.cantidad}
                          onChange={e => setCant(key(l), e.target.value)} className="w-24 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-right text-white outline-none" /></td>
                        <td className="text-right"><button onClick={() => setLineas(ls => ls.filter(x => key(x) !== key(l)))} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="border-t border-white/10"><td className="py-1.5 text-slate-400">Total unidades</td><td className="text-right text-white font-semibold">{total}</td><td></td></tr></tfoot>
                </table>
              )}
            </div>
          </div>

          <div>
            <label className="block text-slate-400 text-xs mb-1">Observaciones</label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} className={inp} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-white/10 text-slate-300 text-sm hover:bg-white/15">Cancelar</button>
          <button onClick={transferir} disabled={guardando} className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700 flex items-center gap-2">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Transferir e imprimir remito
          </button>
        </div>
      </div>
    </div>
  );
}
